import { useEffect } from 'react'
import create from 'zustand'
import { mapToWorkspaceList } from 'api/mappers/workspace'
import { mapToOrganizationList } from 'api/mappers/organization'
import { useGetWorkspaces } from 'api/endpoints/workspaces'
import {
  useGetOrganizations,
  useGetOrganizationUserProfiles,
  useGetOrganizationWorkspaceUserProfiles,
} from 'api/endpoints/organizations'
import {
  OrganizationsResponse,
  Organization as OrganizationModal,
} from 'lib/generated/api'
import { Organization, User, Workspace } from 'types/entities'
import {
  OrganizationPortalAdmin,
  OrganizationType,
  WorkspacePortalAdmin,
} from '../lib/generated/api'
import { DEFAULT_LOCALE, Locale } from 'types/locale'

export type AttachedWorkspaces = {
  id: string | undefined
  name: string | undefined
}

export interface StoreState {
  organization: Organization
  setOrganization: (organization: Organization) => void
  deleteOrganization: () => void
  deleteRequestedWorkspaceId: () => void
  workspace: Workspace
  setWorkspace: (workspace: Workspace) => void
  attachedWorkspaces: Array<AttachedWorkspaces>
  setAttachedWorkspaces: (attachedWorkspaces: Array<AttachedWorkspaces>) => void
  attachedOrganizations: Array<Organization>
  setAttachedOrganizations: (attachedOrganizations: Array<Organization>) => void
  userInfo: User
  setUserInfo: (userInfo: User) => void
  organizationUserProfiles: OrganizationPortalAdmin[]
  setOrganizationUserProfiles: (
    organizationPortalAdmins: OrganizationPortalAdmin[]
  ) => void
  organizationWorkspaceUserProfiles: WorkspacePortalAdmin[]
  setOrganizationWorkspaceUserProfiles: (
    organizationWorkspaceUserProfiles: WorkspacePortalAdmin[]
  ) => void
  requestedWorkspaceId: string
  setRequestedWorkspaceId: (workspaceId: string) => void
  ritualLogoType?: OrganizationType
  setRitualLogoType: (ritualLogoType?: OrganizationType) => void
}

const emptyOrganization: Organization = {
  id: undefined,
  name: undefined,
  type: OrganizationType.Company,
  iconUrl: undefined,
}

const emptyWorkspace: Workspace = {
  id: undefined,
  name: undefined,
  type: undefined,
  organizationId: undefined,
}

const emptyUserInfo: User = {
  email: undefined,
  role: undefined,
}

export const useStore = create<StoreState>(set => {
  const persistedOrganizationSerialized =
    localStorage.getItem('organization') || ''
  const persistedOrganization =
    persistedOrganizationSerialized &&
    JSON.parse(persistedOrganizationSerialized)
  const persistedWorkspace = localStorage.getItem('requestedWorkspaceId') || ''

  return {
    organization: (persistedOrganization || emptyOrganization) as Organization,
    setOrganization: organization => {
      localStorage.setItem('organization', JSON.stringify(organization))
      set({ organization })
    },
    deleteOrganization: () => {
      localStorage.setItem('organization', JSON.stringify(emptyOrganization))
      set({ organization: emptyOrganization })
    },
    deleteRequestedWorkspaceId: () => {
      localStorage.setItem('requestedWorkspaceId', '')
      set({ requestedWorkspaceId: '' })
    },
    attachedOrganizations: [],
    setAttachedOrganizations: attachedOrganizations =>
      set({ attachedOrganizations }),
    deleteAttachedOrganizations: set({ attachedOrganizations: [] }),
    workspace: (persistedWorkspace || emptyWorkspace) as Workspace,
    setWorkspace: workspace => set({ workspace }),
    deleteWorkspace: set({ workspace: emptyWorkspace }),
    attachedWorkspaces: [],
    setAttachedWorkspaces: attachedWorkspaces => set({ attachedWorkspaces }),
    deleteAttachedWorkspaces: set({ attachedWorkspaces: [] }),
    userInfo: emptyUserInfo,
    setUserInfo: userInfo => set({ userInfo }),
    deleteUserInfo: set({ userInfo: emptyUserInfo }),
    organizationUserProfiles: [],
    setOrganizationUserProfiles: organizationUserProfiles =>
      set({ organizationUserProfiles }),
    organizationWorkspaceUserProfiles: [],
    setOrganizationWorkspaceUserProfiles: organizationWorkspaceUserProfiles =>
      set({ organizationWorkspaceUserProfiles }),
    requestedWorkspaceId: persistedWorkspace,
    setRequestedWorkspaceId: requestedWorkspaceId => {
      localStorage.setItem('requestedWorkspaceId', requestedWorkspaceId)
      set({ requestedWorkspaceId })
    },
    ritualLogoType: undefined,
    setRitualLogoType: ritualLogoType => set({ ritualLogoType }),
  }
})

// Sets necessary data in the global store, make sure the user is authenticated before this
export const useHydrateStore = (externalUserId: string) => {
  const organizationId = useStore(state => state.organization.id) as string
  const workspaceId = useStore(state => state.workspace.id)

  const setWorkspace = useStore(state => state.setWorkspace)
  const setAttachedWorkspaces = useStore(state => state.setAttachedWorkspaces)
  const organization = useStore(state => state.organization)
  const setAttachedOrganizations = useStore(
    state => state.setAttachedOrganizations
  )
  const setOrganization = useStore(state => state.setOrganization)
  const userInfo = useStore(state => state.userInfo)
  const setUserInfo = useStore(state => state.setUserInfo)
  const setOrganizationProfiles = useStore(
    state => state.setOrganizationUserProfiles
  )
  const setOrganizationWorkspaceUserProfiles = useStore(
    state => state.setOrganizationWorkspaceUserProfiles
  )
  const requestedWorkspaceId = useStore(state => state.requestedWorkspaceId)

  const initOrganization = (data: OrganizationModal[]) => {
    if (data?.length && !organization.id) {
      const organization = mapToOrganizationList(data)[0]
      setOrganization(organization)
    }
  }
  const organizations = useGetOrganizations(
    {},
    {
      onSuccess: (data: OrganizationsResponse) => {
        const organizations = data?.results
        if (organizations) {
          initOrganization(organizations)
        }
      },
    }
  )
  const { data: workspacesData } = useGetWorkspaces({
    organizationId,
    limit: 100,
  })
  const { data: organizationUserProfiles } = useGetOrganizationUserProfiles({
    organizationId,
    externalUserId,
  })
  const {
    data: organizationWorkspaceUserProfiles,
  } = useGetOrganizationWorkspaceUserProfiles({
    externalUserId,
    organizationId: organization.id!,
  })

  useEffect(() => {
    if (organizations.data?.results?.length) {
      const data = mapToOrganizationList(organizations.data.results)
      setAttachedOrganizations(data)
    }
  }, [
    organizations.data,
    setOrganization,
    setAttachedOrganizations,
    organization.id,
  ])

  useEffect(() => {
    if (workspacesData?.results.length) {
      const data = mapToWorkspaceList(workspacesData)
      const requestedWorkspace = data.find(
        workspace => workspace.id === requestedWorkspaceId
      )

      setWorkspace(requestedWorkspace || data[0])

      setAttachedWorkspaces(
        data.map(workspace => ({
          id: workspace.id,
          name: workspace.name,
        }))
      )
    }
  }, [
    workspacesData,
    setWorkspace,
    setAttachedWorkspaces,
    requestedWorkspaceId,
  ])

  useEffect(() => {
    if (!userInfo.role && organizationUserProfiles?.results?.length) {
      const firstResult = organizationUserProfiles!.results[0]
      setUserInfo({
        firstName: firstResult.firstName,
        lastName: firstResult.lastName,
        email: firstResult.email,
        role: firstResult.role,
        locale: (firstResult.locale || DEFAULT_LOCALE) as Locale,
      })
    }
  }, [organizationUserProfiles, setUserInfo, userInfo.role])

  useEffect(() => {
    if (organizationWorkspaceUserProfiles?.results?.length) {
      const activePortalAdmin = organizationWorkspaceUserProfiles?.results.find(
        portalAdmin => portalAdmin.workspaceId === workspaceId
      )
      if (activePortalAdmin) {
        setUserInfo({
          portalAdminId: activePortalAdmin.portalAdminId,
          firstName: activePortalAdmin.firstName,
          lastName: activePortalAdmin.lastName,
          email: activePortalAdmin.email,
          role: activePortalAdmin.role,
          locale: (activePortalAdmin.locale || DEFAULT_LOCALE) as Locale,
        })
      }
    }
  }, [organizationWorkspaceUserProfiles, workspaceId, setUserInfo])

  useEffect(() => {
    if (externalUserId && organizationUserProfiles?.results?.length) {
      const results = organizationUserProfiles.results
      setOrganizationProfiles(results)
    }
  }, [externalUserId, organizationUserProfiles, setOrganizationProfiles])

  useEffect(() => {
    if (organizationWorkspaceUserProfiles?.results?.length && organization.id) {
      const data = organizationWorkspaceUserProfiles.results
      setOrganizationWorkspaceUserProfiles(data)
    }
  }, [
    organizationWorkspaceUserProfiles,
    organization.id,
    setOrganizationWorkspaceUserProfiles,
  ])
}
